/**
 * AddToExpertiseModal — Modale "Ajouter un Master à cette expertise" (Paquet D).
 *
 * Étape 4 cleanup (Immeuble) / Étape 6 cleanup (Fiscal) :
 *   Le payload d'activation est désormais vide. Les profils détaillés vivent
 *   dans leurs collections séparées :
 *     - Fiscal   → /api/fiscal-dossiers
 *     - Immeuble → /api/immeuble-dossiers
 *
 * Extrait de MasterClientsList.jsx (Batch #14.221).
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { X, Loader2, Search } from "lucide-react";
import { toast } from "@/lib/toast";
import { API, authH } from "@/api";


export default function AddToExpertiseModal({ expertise, existingMasterIds, onClose, onAdded }) {
  const [allMasters, setAllMasters] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState("");
  const [saving, setSaving] = useState(false);
  const [step, setStep] = useState(1);

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/masters`, { headers: authH() });
        setAllMasters(r.data?.items || []);
      } catch {
        toast.error("Erreur chargement masters");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const candidates = allMasters.filter(
    (m) => !existingMasterIds.includes(m.id) &&
      (search === "" || (m.couple_display_name || "").toLowerCase().includes(search.toLowerCase()) ||
       (m.primary_email || "").toLowerCase().includes(search.toLowerCase()))
  );

  const selectedMaster = allMasters.find((m) => m.id === selectedId);

  const goToStep2 = () => {
    if (!selectedId) { toast.error("Sélectionnez un Master"); return; }
    setStep(2);
  };

  const submit = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/masters/${selectedId}/expertises/${expertise}/activate`, {}, { headers: authH() });
      toast.success(`Fiche ${expertise} créée avec succès`);
      onAdded();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur activation");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-2xl shadow-xl max-w-2xl w-full p-6 max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="font-bold text-lg">
              {step === 1 ? `Ajouter à l'expertise ${expertise}` : `Fiche ${expertise} — détails`}
            </h3>
            <p className="text-[11px] text-slate-500">
              {step === 1
                ? "Sélectionnez un dossier Master existant. Ces données NE remontent PAS au Master."
                : `Master sélectionné : ${selectedMaster?.couple_display_name || "?"} · les champs ci-dessous sont propres au volet ${expertise}.`}
            </p>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded" data-testid="add-to-exp-close">
            <X className="w-4 h-4" />
          </button>
        </div>

        {step === 1 && (
          <>
            <div className="relative mb-3">
              <Search className="w-4 h-4 absolute left-3 top-2.5 text-slate-400" />
              <input
                placeholder="Rechercher un Master par nom ou email…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9 pr-3 py-2 border rounded-lg text-sm w-full"
                data-testid="add-to-exp-search"
              />
            </div>

            <div className="overflow-y-auto flex-1 border border-slate-200 rounded-lg" data-testid="add-to-exp-list">
              {loading ? (
                <div className="text-center py-6"><Loader2 className="inline w-5 h-5 animate-spin text-slate-400" /></div>
              ) : candidates.length === 0 ? (
                <div className="text-center py-6 text-sm text-slate-400 italic">
                  {allMasters.length === 0
                    ? "Aucun Master créé. Allez d'abord sur la tuile Master pour créer un dossier."
                    : "Tous les Masters sont déjà rattachés à cette expertise."}
                </div>
              ) : (
                candidates.map((m) => (
                  <label
                    key={m.id}
                    className={`flex items-center gap-3 px-3 py-2.5 border-b border-slate-100 cursor-pointer hover:bg-slate-50 ${selectedId === m.id ? "bg-indigo-50" : ""}`}
                    data-testid={`add-to-exp-row-${m.id}`}
                  >
                    <input
                      type="radio"
                      name="master-pick"
                      checked={selectedId === m.id}
                      onChange={() => setSelectedId(m.id)}
                      className="text-indigo-600"
                    />
                    <div className="min-w-0 flex-1">
                      <div className="font-semibold text-slate-800 text-sm truncate">{m.couple_display_name || "(sans nom)"}</div>
                      <div className="text-[11px] text-slate-500">{m.primary_name} {m.secondary_name ? `+ ${m.secondary_name}` : ""}{m.primary_email ? ` · ${m.primary_email}` : ""}</div>
                    </div>
                  </label>
                ))
              )}
            </div>

            <div className="flex justify-between items-center gap-2 mt-4 pt-3 border-t border-slate-100">
              <Link to="/admin/masters" className="text-xs text-indigo-600 hover:text-indigo-800 underline">
                ➕ Créer un nouveau Master
              </Link>
              <div className="flex gap-2">
                <button onClick={onClose} className="px-3 py-1.5 text-sm rounded-lg border">Annuler</button>
                <button
                  onClick={goToStep2}
                  disabled={!selectedId}
                  className="px-3 py-1.5 text-sm rounded-lg bg-indigo-600 text-white disabled:opacity-40 inline-flex items-center gap-1.5"
                  data-testid="add-to-exp-next"
                >
                  Suivant → champs spécifiques
                </button>
              </div>
            </div>
          </>
        )}

        {step === 2 && selectedMaster && (
          <>
            <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 mb-4 text-xs" data-testid="add-to-exp-master-info">
              <div className="font-semibold text-slate-700 mb-1">
                {selectedMaster.couple_display_name}
              </div>
              <div className="text-slate-500">
                {selectedMaster.primary_name}
                {selectedMaster.secondary_name ? ` + ${selectedMaster.secondary_name}` : ""}
                {selectedMaster.primary_email ? ` · ${selectedMaster.primary_email}` : ""}
              </div>
              <div className="text-[10px] text-slate-400 italic mt-1">
                ⚠️ Les champs ci-dessous sont propres au volet {expertise} — ils ne modifient pas le Master.
              </div>
            </div>

            <div className="overflow-y-auto flex-1">
              {expertise === "fiscal" && (
                <div className="text-sm text-slate-600 bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <p className="font-semibold mb-1">ℹ️ Création de fiche fiscale déplacée</p>
                  <p className="text-xs">
                    Le dossier fiscal a sa propre interface dédiée.{" "}
                    <Link to="/admin/fiscal-dossiers" className="text-blue-700 underline font-semibold">
                      Aller sur Dossiers Fiscaux
                    </Link>{" "}
                    pour créer un dossier avec NAS, sources de revenus, déductions et enfants.
                  </p>
                </div>
              )}
              {expertise === "immeuble" && (
                <div className="text-sm text-slate-600 bg-emerald-50 border border-emerald-200 rounded-lg p-4" data-testid="add-to-exp-immeuble-info">
                  <p className="font-semibold mb-1">ℹ️ Création de fiche immeuble déplacée</p>
                  <p className="text-xs">
                    Le dossier immeuble déclaratif a sa propre interface dédiée.{" "}
                    <Link to="/admin/immeuble-dossiers" className="text-emerald-700 underline font-semibold">
                      Aller sur Dossiers Immeubles
                    </Link>{" "}
                    pour créer un dossier avec NEQ, structure fiscale, propriétés, appartements et locataires.
                  </p>
                  <p className="text-[11px] text-slate-500 italic mt-2">
                    Cliquer sur « Activer » ci-dessous active uniquement l'expertise (création du dossier Google Drive associé).
                  </p>
                </div>
              )}
              {expertise !== "fiscal" && expertise !== "immeuble" && (
                <div className="text-sm text-slate-500 italic">
                  Aucun champ spécifique requis pour {expertise}.
                </div>
              )}
            </div>

            <div className="flex justify-between items-center gap-2 mt-4 pt-3 border-t border-slate-100">
              <button
                onClick={() => setStep(1)}
                className="text-xs text-slate-500 hover:text-slate-700"
                data-testid="add-to-exp-back"
              >
                ← Retour à la sélection
              </button>
              <div className="flex gap-2">
                <button onClick={onClose} className="px-3 py-1.5 text-sm rounded-lg border">Annuler</button>
                <button
                  onClick={submit}
                  disabled={saving}
                  className="px-3 py-1.5 text-sm rounded-lg bg-emerald-600 text-white disabled:opacity-40 inline-flex items-center gap-1.5"
                  data-testid="add-to-exp-confirm"
                >
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                  Créer la fiche {expertise}
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
